/**
 * Calendar Service
 * Handles calendar_events and timetable management
 */

import { CalendarEventsRecord, TimetableRecord } from '@/types/collections';
import { createRepository } from '@/services/repository';

class CalendarService {
  private eventsRepo = createRepository<CalendarEventsRecord>('calendar_events');
  private timetableRepo = createRepository<TimetableRecord>('timetable');
  private resolvedEventSort: 'start_date' | 'startDate' | '-created' | null = null;
  private resolvedEventFilterField: 'start_date' | 'startDate' | null = null;
  private resolvingEventStrategy: Promise<void> | null = null;

  setUserId(userId: string): void {
    this.eventsRepo.setUserId(userId);
    this.timetableRepo.setUserId(userId);
  }

  private setResolvedEventStrategy(
    sort: 'start_date' | 'startDate' | '-created' | null,
    field: 'start_date' | 'startDate' | null
  ): void {
    this.resolvedEventSort = sort;
    this.resolvedEventFilterField = field;
  }

  private isSortOrFilterIssue(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      message.includes('invalid sort field') ||
      message.includes('invalid filter') ||
      message.includes('Failed to load resource') ||
      message.includes('Something went wrong while processing your request')
    );
  }

  private async resolveEventStrategy(): Promise<void> {
    if (this.resolvedEventSort) return;
    if (this.resolvingEventStrategy) return this.resolvingEventStrategy;

    this.resolvingEventStrategy = (async () => {
      const attempts: Array<{ sort: 'start_date' | 'startDate' | '-created'; field?: 'start_date' | 'startDate' }> = [
        { sort: 'start_date', field: 'start_date' },
        { sort: 'startDate', field: 'startDate' },
        { sort: '-created' },
      ];

      let lastError: unknown = null;

      for (const attempt of attempts) {
        try {
          await this.eventsRepo.list({
            sort: attempt.sort,
            page: 1,
            perPage: 1,
            skipTotal: true,
          });

          this.setResolvedEventStrategy(attempt.sort, attempt.field || null);
          return;
        } catch (error) {
          lastError = error;
          if (!this.isSortOrFilterIssue(error)) {
            throw error;
          }
        }
      }

      throw lastError instanceof Error ? lastError : new Error('Failed to resolve calendar strategy');
    })().finally(() => {
      this.resolvingEventStrategy = null;
    });

    return this.resolvingEventStrategy;
  }

  // Events API
  async listEvents(options?: { month?: number; year?: number }) {
    if (!options) {
      return this.eventsRepo.list({ sort: '-created' });
    }

    const monthStart = options
      ? `${options.year}-${String(options.month).padStart(2, '0')}-01`
      : null;
    await this.resolveEventStrategy();

    const activeSort = this.resolvedEventSort || '-created';
    const activeField = this.resolvedEventFilterField;
    const filter = monthStart && activeField ? `${activeField} >= "${monthStart}"` : undefined;

    try {
      return await this.eventsRepo.list({
        filter,
        sort: activeSort,
      });
    } catch (error) {
      if (!this.isSortOrFilterIssue(error)) {
        throw error;
      }

      this.setResolvedEventStrategy(null, null);
      await this.resolveEventStrategy();

      const retrySort = this.resolvedEventSort || '-created';
      const retryField = this.resolvedEventFilterField;
      const retryFilter = monthStart && retryField ? `${retryField} >= "${monthStart}"` : undefined;

      return this.eventsRepo.list({
        filter: retryFilter,
        sort: retrySort,
      });
    }
  }

  async getEvent(id: string) {
    return this.eventsRepo.get(id);
  }

  async createEvent(data: Partial<CalendarEventsRecord>) {
    return this.eventsRepo.create(data);
  }

  async updateEvent(id: string, data: Partial<CalendarEventsRecord>) {
    return this.eventsRepo.update(id, data);
  }

  async deleteEvent(id: string) {
    return this.eventsRepo.delete(id);
  }

  // Timetable API
  async getTimetable() {
    return this.timetableRepo.list({ sort: 'day_of_week,start_time' });
  }

  async addTimetableEntry(data: Partial<TimetableRecord>) {
    return this.timetableRepo.create(data);
  }

  async updateTimetableEntry(id: string, data: Partial<TimetableRecord>) {
    return this.timetableRepo.update(id, data);
  }

  async deleteTimetableEntry(id: string) {
    return this.timetableRepo.delete(id);
  }
}

export const calendarService = new CalendarService();

